import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Minus, Info, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { MembershipTiers } from "../components/Illustrations";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import {
  MEMBERSHIP_PLANS, MEMBERSHIP_COMPARISON, MEMBERSHIP_WHO,
  MEMBERSHIP_FAQ, PREMIUM_ELIGIBILITY,
} from "../data/content";

function Cell({ value }) {
  if (value === true) return <Check className="mx-auto h-4 w-4 text-[color:var(--brand-green)]" />;
  if (value === false) return <Minus className="mx-auto h-4 w-4 text-[color:var(--ink-soft)] opacity-50" />;
  return <span className="text-xs font-medium text-[color:var(--brand-teal)]">{value}</span>;
}

export default function Membership() {
  return (
    <>
      <Seo title="Membership" description="StUpEco Hub membership — Free, Standard and Premium. Membership philosophy, a full comparison table, who each plan is for, automatic Premium eligibility and FAQs. Pricing announced later." path="/membership" />

      {/* Hero + philosophy */}
      <section className="relative pt-32 pb-12 lg:pt-40" data-testid="membership-hero">
        <div className="grid-lines absolute inset-0 -z-10 opacity-50" />
        <div className="container-hub grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal><span className="eyebrow"><span className="brand-bar h-1.5 w-1.5 rounded-full" /> Membership</span></Reveal>
            <Reveal delay={0.05}><h1 className="font-display mt-5 text-4xl font-semibold leading-[1.02] tracking-[-0.03em] text-[color:var(--ink)] sm:text-5xl lg:text-6xl">Membership that grows with your business.</h1></Reveal>
            <Reveal delay={0.1}><p className="mt-6 max-w-xl text-base leading-relaxed text-[color:var(--ink-soft)] sm:text-lg">Membership exists to keep the network trusted and valuable. Free lets anyone explore; paid tiers unlock participation and visibility — so the members investing in the Hub get the most from it. Pricing will be announced later.</p></Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}><div className="hub-card rounded-3xl bg-[color:var(--soft)] p-8"><MembershipTiers className="w-full" /></div></Reveal>
          </div>
        </div>
      </section>

      {/* Plan cards */}
      <section className="py-14" data-testid="membership-plans">
        <div className="container-hub grid grid-cols-1 gap-5 lg:grid-cols-3">
          {MEMBERSHIP_PLANS.map((plan, i) => (
            <Reveal key={plan.name} delay={i * 0.08}>
              <div data-testid={`membership-plan-${plan.name.toLowerCase()}`} className={`relative flex h-full flex-col rounded-3xl p-8 lg:p-10 ${plan.highlight ? "bg-[color:var(--invert-bg)] text-white" : "hub-card"}`}>
                {plan.highlight && <span className="brand-bar absolute right-8 top-8 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white">Most popular</span>}
                <p className={`font-display text-3xl font-semibold tracking-tight ${plan.highlight ? "text-white" : "text-[color:var(--ink)]"}`}>{plan.name}</p>
                <p className={`mt-1.5 text-sm ${plan.highlight ? "text-white/70" : "text-[color:var(--ink-soft)]"}`}>{plan.tagline}</p>
                {plan.inheritsLabel && <p className={`mt-6 text-xs font-semibold uppercase tracking-[0.14em] ${plan.highlight ? "text-white/60" : "text-[color:var(--ink-soft)]"}`}>{plan.inheritsLabel}</p>}
                <div className={`my-6 h-px w-full ${plan.highlight ? "bg-white/15" : "bg-[color:var(--hairline)]"} ${plan.inheritsLabel ? "mt-3" : ""}`} />
                <ul className="flex-1 space-y-3.5">
                  {plan.includes.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm">
                      <Check className={`mt-0.5 h-4 w-4 flex-shrink-0 ${plan.highlight ? "text-[color:var(--brand-green)]" : "text-[color:var(--brand-teal)]"}`} />
                      <span className={plan.highlight ? "text-white/85" : "text-[color:var(--ink)]"}>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/join" data-testid={`membership-cta-${plan.name.toLowerCase()}`} className={`group mt-8 inline-flex items-center justify-center gap-1.5 rounded-full px-6 py-3.5 text-sm font-semibold transition-transform duration-200 hover:-translate-y-0.5 ${plan.highlight ? "bg-white text-[color:var(--ink)]" : "bg-[color:var(--invert-bg)] text-white"}`}>
                  {plan.name === "Free" ? "Get started" : `Choose ${plan.name}`}<ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Who is each plan for */}
      <section className="bg-soft py-16 lg:py-24" data-testid="membership-who">
        <div className="container-hub">
          <Reveal><span className="eyebrow">Who is it for?</span></Reveal>
          <Reveal delay={0.05}><h2 className="font-display mt-4 text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">Find the plan that fits your stage.</h2></Reveal>
          <div className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-3">
            {MEMBERSHIP_WHO.map((w, i) => (
              <Reveal key={w.plan} delay={i * 0.08}>
                <div className="hub-card h-full rounded-2xl p-7">
                  <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: w.c, backgroundColor: `color-mix(in srgb, ${w.c} 12%, transparent)` }}>{w.plan}</span>
                  <p className="mt-4 text-base leading-relaxed text-[color:var(--ink)]">{w.who}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-16 lg:py-24" data-testid="membership-comparison">
        <div className="container-hub">
          <Reveal><span className="eyebrow">Compare plans</span></Reveal>
          <Reveal delay={0.05}><h2 className="font-display mt-4 text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">Everything, side by side.</h2></Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 overflow-hidden rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--surface)]">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[color:var(--hairline)] bg-[color:var(--soft)]">
                      <th className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide text-[color:var(--ink-soft)]">Feature</th>
                      <th className="px-5 py-4 text-center text-sm font-semibold text-[color:var(--ink)]">Free</th>
                      <th className="px-5 py-4 text-center text-sm font-semibold text-[color:var(--ink)]">Standard</th>
                      <th className="px-5 py-4 text-center text-sm font-semibold text-[color:var(--ink)]">Premium</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MEMBERSHIP_COMPARISON.map((row) => (
                      <tr key={row.feature} className="border-b border-[color:var(--hairline)] last:border-0 hover:bg-[color:var(--soft)]">
                        <td className="px-5 py-3.5 text-left font-medium text-[color:var(--ink)]">{row.feature}</td>
                        <td className="px-5 py-3.5 text-center"><Cell value={row.free} /></td>
                        <td className="px-5 py-3.5 text-center"><Cell value={row.standard} /></td>
                        <td className="px-5 py-3.5 text-center"><Cell value={row.premium} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Upgrade journey */}
      <section className="bg-soft py-16 lg:py-24" data-testid="membership-journey">
        <div className="container-hub">
          <Reveal><span className="eyebrow">Upgrade journey</span></Reveal>
          <Reveal delay={0.05}><h2 className="font-display mt-4 text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">How members grow through the tiers.</h2></Reveal>
          <div className="mt-12 flex flex-col gap-4 lg:flex-row lg:items-center">
            {[
              { step: "Explore", plan: "Free", text: "Browse the network and read discussions." },
              { step: "Participate", plan: "Standard", text: "List, profile, message and cross-sell." },
              { step: "Lead", plan: "Premium", text: "Get featured, verified and discovered first." },
            ].map((s, i, arr) => (
              <div key={s.step} className="flex flex-1 flex-col gap-4 lg:flex-row lg:items-center">
                <Reveal delay={i * 0.1}>
                  <div className="hub-card rounded-2xl p-6">
                    <span className="font-display text-sm font-semibold text-[color:var(--ink-soft)]">Step {i + 1}</span>
                    <p className="font-display mt-1 text-xl font-semibold text-[color:var(--ink)]">{s.step}</p>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-[color:var(--brand-teal)]">{s.plan}</p>
                    <p className="mt-3 text-sm text-[color:var(--ink-soft)]">{s.text}</p>
                  </div>
                </Reveal>
                {i < arr.length - 1 && <ArrowRight className="mx-auto hidden h-5 w-5 flex-shrink-0 text-[color:var(--ink-soft)] lg:block" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium eligibility */}
      <section className="py-16 lg:py-24" data-testid="membership-eligibility">
        <div className="container-hub">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-4">
              <Reveal>
                <span className="eyebrow">Automatic Premium</span>
                <h2 className="font-display mt-4 text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">Premium, included.</h2>
                <p className="mt-4 text-base leading-relaxed text-[color:var(--ink-soft)]">Some members receive Premium Hub membership automatically as part of their StUpEco relationship.</p>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {PREMIUM_ELIGIBILITY.map((e, i) => (
                  <Reveal key={e.title} delay={i * 0.08}>
                    <div className="hub-card h-full rounded-2xl p-6">
                      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[color:var(--brand-red)]/10 text-[color:var(--brand-red)]"><Info className="h-4 w-4" /></span>
                      <h3 className="font-display mt-4 text-base font-semibold text-[color:var(--ink)]">{e.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-[color:var(--ink-soft)]">{e.text}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-soft py-16 lg:py-24" data-testid="membership-faq">
        <div className="container-hub grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal>
              <span className="eyebrow">FAQ</span>
              <h2 className="font-display mt-4 text-3xl font-semibold tracking-[-0.02em] text-[color:var(--ink)] sm:text-4xl">Common questions.</h2>
              <p className="mt-4 text-sm text-[color:var(--ink-soft)]">Still curious? <Link to="/contact" className="font-semibold text-[color:var(--ink)] underline-offset-4 hover:underline">Get in touch</Link>.</p>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.08}>
              <Accordion type="single" collapsible className="w-full" data-testid="membership-faq-accordion">
                {MEMBERSHIP_FAQ.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-[color:var(--hairline)]">
                    <AccordionTrigger className="text-left font-display text-base font-semibold text-[color:var(--ink)] hover:no-underline">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-[color:var(--ink-soft)]">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
